<template>
	<UHeader
		mode="drawer"
		@update:open="handleMobileHeaderNavigationOpened($event)"
	>
		<UNavigationMenu
			:items="items"
			color="primary"
			variant="pill"
			:ui="{ childLink: 'hover:bg-accented' }"
		>
			<template #localization>
				<Localization />
			</template>
		</UNavigationMenu>

		<template #left>
			<NuxtLink
				:to="localePath('/')"
				aria-label="Maverix Broadband"
			>
				<LogoFull class="w-auto h-8 sm:h-11 shrink-0 text-highlighted" />
			</NuxtLink>
		</template>

		<template #right>
			<UButton
				v-if="loggedIn"
				:id="HEADER_YOUR_ACCOUNT_BUTTON_ID"
				:label="$t('links.your-account')"
				:to="accountUrl"
				target="_blank"
				size="lg"
				color="primary"
				variant="outline"
				class="hidden sm:inline-flex uppercase"
				@click="trackEvent($event, HEADER_NAV_LOCATION_ID)"
				@mouseenter="trackEvent($event, HEADER_NAV_LOCATION_ID)"
				@focus="trackEvent($event, HEADER_NAV_LOCATION_ID)"
			/>
			<UButton
				v-else
				:id="HEADER_SIGN_IN_BUTTON_ID"
				:label="$t('links.sign-in')"
				:to="localePath('/login')"
				size="lg"
				color="primary"
				variant="outline"
				class="hidden sm:inline-flex uppercase"
				@click="trackEvent($event, HEADER_NAV_LOCATION_ID)"
				@mouseenter="trackEvent($event, HEADER_NAV_LOCATION_ID)"
				@focus="trackEvent($event, HEADER_NAV_LOCATION_ID)"
			/>

			<UButton
				:id="HEADER_CHECK_AVAILABILITY_BUTTON_ID"
				:label="$t('links.check-availability')"
				:to="localePath('/get-fiber')"
				size="lg"
				class="hidden sm:inline-flex uppercase"
				@click="trackEvent($event, HEADER_NAV_LOCATION_ID)"
				@mouseenter="trackEvent($event, HEADER_NAV_LOCATION_ID)"
				@focus="trackEvent($event, HEADER_NAV_LOCATION_ID)"
			/>
		</template>

		<template #body>
			<div class="flex items-center gap-2 pb-4">
				<UButton
					:id="DRAWER_CHECK_AVAILABILITY_BUTTON_ID"
					:label="$t('links.check-availability')"
					:to="localePath('/get-fiber')"
					size="lg"
					color="neutral"
					variant="outline"
					:ui="{
						base: 'text-center inline-flex flex-1 flex-grow justify-center'
					}"
					@click="trackEvent($event, MOBILE_DRAWER_NAV_LOCATION_ID)"
					@mouseenter="trackEvent($event, MOBILE_DRAWER_NAV_LOCATION_ID)"
					@focus="trackEvent($event, MOBILE_DRAWER_NAV_LOCATION_ID)"
				/>
				<UButton
					v-if="loggedIn"
					:id="DRAWER_YOUR_ACCOUNT_BUTTON_ID"
					:label="$t('links.your-account')"
					:to="accountUrl"
					target="_blank"
					size="lg"
					color="neutral"
					variant="outline"
					:ui="{ base: 'text-center inline-flex justify-center w-2/5' }"
					@click="trackEvent($event, MOBILE_DRAWER_NAV_LOCATION_ID)"
					@mouseenter="trackEvent($event, MOBILE_DRAWER_NAV_LOCATION_ID)"
					@focus="trackEvent($event, MOBILE_DRAWER_NAV_LOCATION_ID)"
				/>
				<UButton
					v-else
					:id="DRAWER_SIGN_IN_BUTTON_ID"
					:label="$t('links.sign-in')"
					:to="localePath('/login')"
					target="_self"
					size="lg"
					color="neutral"
					variant="outline"
					:ui="{ base: 'text-center inline-flex justify-center w-2/5' }"
					@click="trackEvent($event, MOBILE_DRAWER_NAV_LOCATION_ID)"
					@mouseenter="trackEvent($event, MOBILE_DRAWER_NAV_LOCATION_ID)"
					@focus="trackEvent($event, MOBILE_DRAWER_NAV_LOCATION_ID)"
				/>
			</div>
			<UNavigationMenu
				v-model="activeMobileNavParents"
				:items="mobileItems"
				color="secondary"
				variant="pill"
				orientation="vertical"
				class="-mx-2.5"
			>
				<template #localization>
					<Localization />
				</template>
			</UNavigationMenu>
		</template>
	</UHeader>
</template>

<script setup lang="ts">
import { useAuth } from '@/composables/useAuth'
import { useGA4Tracking } from '@/composables/useGA4Tracking'
import { useNavigationTracking } from '@/composables/useNavigationTracking'
import {
	DRAWER_CHECK_AVAILABILITY_BUTTON_ID,
	DRAWER_SIGN_IN_BUTTON_ID,
	DRAWER_YOUR_ACCOUNT_BUTTON_ID,
	HEADER_CHECK_AVAILABILITY_BUTTON_ID,
	HEADER_NAV_LOCATION_ID,
	HEADER_SIGN_IN_BUTTON_ID,
	HEADER_YOUR_ACCOUNT_BUTTON_ID,
	MOBILE_DRAWER_NAV_LOCATION_ID
} from '@/constants/ga4/tracking-ids/layout/header'
import { HELP_CENTER_BASE_URL_EN } from '@/constants/index.js'
import type { NavigationMenuItem } from '@nuxt/ui'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

const localePath = useLocalePath()
const { t } = useI18n({
	useScope: 'global'
})

const route = useRoute()

const {
	loggedIn, accountUrl, getAuth
} = useAuth()

const { trackEvent } = useGA4Tracking()

const {
	setupHeaderNavigationTracking,
	handleMobileHeaderNavigationTracking,
	handleMobileHeaderNavigationOpened
} = useNavigationTracking()

// the UNavigationMenu v-model returns the active parent items which is all we need
// since we don't need hover tracking on mobile
const activeMobileNavParents = ref()

const items = computed<NavigationMenuItem[]>(() => [
	{
		label: t('links.services'),
		children: [
			{
				label: t('links.residential'),
				description: t('header.residential-description'),
				to: localePath('/residential-internet'),
				active: String (route.name).startsWith('residential-internet')
			},
			{
				label: t('links.business'),
				description: t('header.business-description'),
				to: localePath('/business-internet'),
				active: String (route.name).startsWith('business')
			},
			{
				label: t('links.service-areas'),
				description: t('header.service-areas-description'),
				to: localePath('/service-areas'),
				active: String (route.name).startsWith('service-areas')
			}
		],
		active: String (route.name).startsWith('residential-internet') || String (route.name).startsWith('business') || String (route.name).startsWith('service-areas')
	},
	{
		label: t('links.construction'),
		children: [
			{
				label: t('links.installation'),
				description: t('header.installation-description'),
				to: localePath('/installation-overview'),
				active: String (route.name).startsWith('installation-overview')
			},
			{
				label: t('links.eight-one-one'),
				description: t('header.eight-one-one-description'),
				to: localePath('/811'),
				active: String (route.name).startsWith('811')
			}
		],
		active: String (route.name).startsWith('installation-overview') || String (route.name).startsWith('811')
	},
	{
		label: t('links.about'),
		children: [
			{
				label: t('links.about-us'),
				description: t('header.about-us-description'),
				to: localePath('/about'),
				active: String (route.name).startsWith('about')
			},
			{
				label: t('links.blog'),
				description: t('header.blog-description'),
				to: localePath('/blog'),
				active: String (route.name).startsWith('blog')
			},
			{
				label: t('links.fiber-news'),
				description: t('header.fiber-news-description'),
				to: localePath('/press'),
				active: String (route.name).startsWith('fiber-news')
			},
			{
				label: t('links.careers'),
				description: t('header.careers-description'),
				to: localePath('/careers'),
				active: String (route.name).startsWith('careers')
			}
		],
		active: String (route.name).startsWith('about') || String (route.name).startsWith('blog') || String (route.name).startsWith('fiber-news') || String (route.name).startsWith('careers')
	},
	{
		label: t('links.support'),
		children: [
			{
				label: t('links.help-center'),
				description: t('header.help-center-description'),
				to: HELP_CENTER_BASE_URL_EN,
				target: '_blank',
				external: true,
				active: false // This will not be active since it's an external link
			},
			{
				label: t('links.contact-us'),
				description: t('header.contact-us-description'),
				to: localePath('/contact'),
				active: String (route.name).startsWith('contact')
			}
		],
		active: String (route.name).startsWith('contact')
	},
	{
		label: 'Localization',
		slot: 'localization' as const
	}
] satisfies NavigationMenuItem[])

const mobileItems = computed<NavigationMenuItem[]>(() => [
	{
		label: t('links.services'),
		children: [
			{
				label: t('links.residential'),
				description: t('header.residential-description'),
				to: localePath('/residential-internet'),
				active: String (route.name).startsWith('residential-internet')
			},
			{
				label: t('links.business'),
				description: t('header.business-description'),
				to: localePath('/business-internet'),
				active: String (route.name).startsWith('business')
			},
			{
				label: t('links.service-areas'),
				description: t('header.service-areas-description'),
				to: localePath('/service-areas'),
				active: String (route.name).startsWith('service-areas')
			}
		],
		active: String (route.name).startsWith('residential-internet') || String (route.name).startsWith('business') || String (route.name).startsWith('service-areas')
	},
	{
		label: t('links.construction'),
		children: [
			{
				label: t('links.installation'),
				description: t('header.installation-description'),
				to: localePath('/installation-overview'),
				active: String (route.name).startsWith('installation-overview')
			},
			{
				label: t('links.eight-one-one'),
				description: t('header.eight-one-one-description'),
				to: localePath('/811'),
				active: String (route.name).startsWith('811')
			}
		],
		active: String (route.name).startsWith('installation-overview') || String (route.name).startsWith('811')
	},
	{
		label: t('links.about'),
		children: [
			{
				label: t('links.about-us'),
				description: t('header.about-us-description'),
				to: localePath('/about'),
				active: String (route.name).startsWith('about')
			},
			{
				label: t('links.blog'),
				description: t('header.blog-description'),
				to: localePath('/blog'),
				active: String (route.name).startsWith('blog')
			},
			{
				label: t('links.fiber-news'),
				description: t('header.fiber-news-description'),
				to: localePath('/press'),
				active: String (route.name).startsWith('fiber-news')
			},
			{
				label: t('links.careers'),
				description: t('header.careers-description'),
				to: localePath('/careers'),
				active: String (route.name).startsWith('careers')
			}
		],
		active: String (route.name).startsWith('about') || String (route.name).startsWith('blog') || String (route.name).startsWith('fiber-news') || String (route.name).startsWith('careers')
	},
	{
		label: t('links.support'),
		children: [
			{
				label: t('links.help-center'),
				description: t('header.help-center-description'),
				to: HELP_CENTER_BASE_URL_EN,
				target: '_blank',
				external: true,
				active: false // This will not be active since it's an external link
			},
			{
				label: t('links.contact-us'),
				description: t('header.contact-us-description'),
				to: localePath('/contact'),
				active: String (route.name).startsWith('contact')
			}
		],
		active: String (route.name).startsWith('contact')
	},
	{
		label: 'Localization',
		slot: 'localization' as const
	}
] satisfies NavigationMenuItem[])

watch(activeMobileNavParents, newValue => {
	handleMobileHeaderNavigationTracking(mobileItems.value, newValue || [])
})

onMounted(() => {
	getAuth()

	// Setup navigation tracking once DOM is ready
	nextTick(() => {
		setupHeaderNavigationTracking(items.value)
	})
})
</script>
